import * as React from "react";
import { useState } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  PhoneAuthProvider,
  getMultiFactorResolver,
  getAuth,
  signInWithEmailAndPassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { RECAPTCH_CONTAINER_ID } from "../../config/index";
import validator from "validator";

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {"Copyright © "}
      <Link color="inherit" href="https://mui.com/">
        Certificates BO
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

const theme = createTheme();

const getPhoneAuthProviderParams = (
  resolver,
  auth,
  secondRecaptchaContainerIdSuffix
) => {
  // According to docs next lines should be used but there is a firebase auth library bug in which FACTOR_ID is undefined(bug appears as github issue)
  // if (resolver.hints[selectedIndex].factorId ===
  //     auth.PhoneMultiFactorGenerator.FACTOR_ID) {
  const phoneInfoOptions = {
    multiFactorHint: resolver.hints[0], // resolver.hints[selectedIndex] // TODO: in future support more factors and then let the user choose
    session: resolver.session,
  };

  const applicationVerifier = new RecaptchaVerifier( // the racaptcha initialization requires the container's tag id - because the recapcha is used in 2 differnt places - the download certificate which needs reauthentication and on sign in, concatenating the id suffix is done to identify each container
    RECAPTCH_CONTAINER_ID + secondRecaptchaContainerIdSuffix,
    {
      callback: (response) => {
        // reCAPTCHA solved, allow signInWithPhoneNumber.
        // ...
      },
      "expired-callback": () => {
        // Response expired. Ask user to solve reCAPTCHA again.
        // ...
      },
    },
    auth
  );
  return { phoneInfoOptions, applicationVerifier };
};

const verifySMS = (verificationId, resolver) => {
  // Ask user for the SMS verification code.
  let verificationCode = prompt("Please enter verification code");
  var cred = PhoneAuthProvider.credential(verificationId, verificationCode);
  var multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
  // Complete sign-in.
  return resolver.resolveSignIn(multiFactorAssertion);
};

const verifyPhoneByParams = async (auth, resolver, params) => {
  let { phoneInfoOptions, applicationVerifier } = params;
  const phoneAuthProvider = new PhoneAuthProvider(auth);
  // Send SMS verification code
  return await phoneAuthProvider
    .verifyPhoneNumber(phoneInfoOptions, applicationVerifier)
    .then(function (verificationId) {
      return verifySMS(verificationId, resolver);
    })
    .then(function ({ user }) {
      // User successfully signed in with the second factor phone number.
      // onAuthStateChanged is called and state is handled there

      window.localStorage.setItem("auth", "true");
      return user
        .getIdToken(true)
        .then((token) => {
          window.localStorage.setItem("token", token);
        })
        .catch((error) => {});
    })
    .catch((error) => {
      applicationVerifier.clear();
    });
};

const signWithMFA = async (
  auth,
  error,
  secondRecaptchaContainerIdSuffix = "" // the racaptcha initialization requires the container's tag id -
  // because the recapcha is used in 2 differnt places -
  // the download certificate which needs reauthentication and on sign in, concatenating the id suffix is done to identify each container
) => {
  let resolver = getMultiFactorResolver(auth, error);
  let { phoneInfoOptions, applicationVerifier } = getPhoneAuthProviderParams(
    resolver,
    auth,
    secondRecaptchaContainerIdSuffix
  );
  applicationVerifier.render();
  let params = { phoneInfoOptions, applicationVerifier };
  return await verifyPhoneByParams(auth, resolver, params);
};

const handleSignIn = async (event) => {
  event.preventDefault();
  const data = new FormData(event.currentTarget);
  const auth = getAuth();

  await signInWithEmailAndPassword(
    auth,
    data.get("email"),
    data.get("password")
  )
    .then((userCredential) => {
      // Signed in
    })
    .catch((error) => {
      if (error.code === "auth/multi-factor-auth-required") {
        return signWithMFA(auth, error); // same as signWithMFA(auth, error, "") because the sign in recaptcha container tag id is from the config file with no suffix
      } else if (error.code === "auth/wrong-password") {
        // Handle other errors such as wrong password.
        alert("Wrong password");
      }
    });
};

export const handleReauthenticate = async (
  event,
  secondRecaptchaContainerIdSuffix
) => {
  event.preventDefault();
  const data = new FormData(event.currentTarget);
  const auth = getAuth();
  const authCredential = EmailAuthProvider.credential(
    data.get("email"),
    data.get("password")
  );
  await reauthenticateWithCredential(auth.currentUser, authCredential)
    .then((userCredential) => {
      // Signed in
    })
    .catch((error) => {
      if (error.code === "auth/multi-factor-auth-required") {
        return signWithMFA(auth, error, secondRecaptchaContainerIdSuffix);
      } else if (error.code === "auth/wrong-password") {
        // Handle other errors such as wrong password.
        alert("Wrong password");
      }
    });
};
function SignIn() {
  const [emailValue, setEmailValue] = useState("");
  const [isValidEmail, setIsValidEmail] = useState(true);

  const validateEmail = (email) => {
    const isValidEmail = validator.isEmail(email);
    return isValidEmail;
  };

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Sign in
          </Typography>
          <Box
            component="form"
            onSubmit={handleSignIn}
            noValidate
            sx={{ mt: 1 }}
          >
            <TextField
              value={emailValue}
              onChange={(e) => {
                setEmailValue(e.target.value);
                setIsValidEmail(validateEmail(e.target.value));
              }}
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              error={!isValidEmail}
              helperText={!isValidEmail ? "Illegal email" : ""}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
            />
            <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Remember me"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Sign In
            </Button>
            <div id={RECAPTCH_CONTAINER_ID}></div>
            <Grid container></Grid>
          </Box>
        </Box>
        <Copyright sx={{ mt: 8, mb: 4 }} />
      </Container>
    </ThemeProvider>
  );
}

export default SignIn;
